<template>

  <div v-if="loading && !showDashboard" class="full-screen">
    <h1 class="display-3 text-secondary text-shadow">{{ headline }}</h1>
    <h2 class="text-secondary mt-3" style="font-size: 100%">{{ subHeadline }}</h2>

    <UiSpacer :height="20" />

    <button 
      class="start-btn" 
      :class="{ 'gold': !loadingComplete }" 
      :disabled="loadingComplete" 
      @click="startDashboard">
      {{ loadingComplete ? 'Loading...' : 'Get Started' }}
    </button>
  </div>

  <div
  v-if="showDashboard"
  class="admin-shell"
  :class="{ 'sidebar-collapsed': sidebarCollapsed }"
>
  <aside class="admin-sidebar">

    <div class="sidebar-heading">
      <div class="sidebar-brand">
        <h1>
          {{ sidebarCollapsed ? "LLC" : "Lomax Law Connect" }}
        </h1>

        <p>
          {{ sidebarCollapsed ? "Maix" : "Maix Dashboard" }}
        </p>
      </div>

      <button
        type="button"
        class="sidebar-toggle"
        :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="toggleSidebar"
      >
        <span class="material-icons-outlined">
          {{
            sidebarCollapsed
              ? "keyboard_double_arrow_right"
              : "keyboard_double_arrow_left"
          }}
        </span>
      </button>
    </div>

    <nav class="sidebar-navigation">

      <section
        v-for="group in navigationGroups"
        :key="group.id"
        class="sidebar-group"
      >
        <h2 v-if="group.title" class="sidebar-group-heading">
          {{ group.title }}
        </h2>

        <template v-for="item in group.items" :key="item.id">
        <button
          class="sidebar-item"
          :class="{
            active:
              selectedSection === item.id,
            placeholder:
              item.placeholder
          }"
          :aria-disabled="item.placeholder ? 'true' : undefined"
          :title="sidebarCollapsed ? item.title : ''"
          @click="selectSection(item.id)"
        >
          <span class="material-icons-outlined sidebar-icon">
            {{ item.icon }}
          </span>

          <span class="sidebar-item-label">
            {{ item.title }}
          </span>
          <span v-if="reviewCounts[item.id]" class="review-badge" :aria-label="`${reviewCounts[item.id]} new items`">{{ reviewCounts[item.id] }}</span>
        </button>

        </template>
      </section>

      <router-link class="sidebar-item" to="/admin-profile" :title="sidebarCollapsed ? 'My profile' : ''" style="text-decoration: none;">
        <span class="material-icons-outlined sidebar-icon">account_circle</span>
        <span class="sidebar-item-label">My profile</span>
      </router-link>
    </nav>

    <div class="sidebar-footer">

      <button
        class="logout-button"
        :title="sidebarCollapsed ? 'Log Out' : ''"
        @click="handleLogOut"
      >
        <span class="material-icons-outlined">
          logout
        </span>

        <span class="logout-label">
          Log Out
        </span>
      </button>

    </div>

  </aside>

  <main class="admin-content">

    <header class="content-header">

      <div>
        <h2>
          {{ selectedSectionTitle }}
        </h2>

        <p>
          {{ selectedSectionDescription }}
        </p>
      </div>

      <button
        v-if="selectedSectionAction"
        class="primary-action-button"
        @click="performSelectedSectionAction"
      >
        {{ selectedSectionAction }}
      </button>

    </header>

    <section
      class="content-window"
      :class="{
        'legal-content-window': selectedSection === 'legal-terms',
        'texts-content-window': ['maix-texts', 'maix-magic'].includes(selectedSection),
        'request-review-content-window':
          selectedSection === 'requests' && contentMode === 'review'
      }"
    >

      <AttorneySearch v-if="selectedSection === 'attorney-search'" />
      <section
        v-if="selectedSection === 'requests'"
        class="request-section"
      >
        <RequestDetails
          v-if="contentMode === 'list'"
          :refreshKey="requestRefreshKey"
          :key="reviewFilterKey" :initial-status="reviewFilter" @new-count="reviewCounts.requests = $event"
          @review="reviewRequest"
        />

        <RequestDashboard
          v-else-if="
            contentMode === 'review' &&
            selectedRequest
          "
          :request="selectedRequest"
          :adminId="adminId"
          @cancel="returnToSectionList"
          @saved="handleRequestSaved"
          @chat="handleRequestChat"
        />
      </section>

      <section
        v-if="selectedSection === 'submissions'"
        class="submission-section"
      >
        <SubmissionsListWindow
          :key="reviewFilterKey" :initial-status="reviewFilter" @new-count="reviewCounts.submissions = $event"
          :admin-id="adminId"
          :admin-name="adminName"
        />
      </section>

      <section
        v-if="selectedSection === 'watch-trends'"
        class="submission-section"
      >
        <TrendsListWindow
          :admin-id="adminId"
          :admin-name="adminName"
        />
      </section>

      <section
        v-if="selectedSection === 'archives'"
        class="archive-section"
      >
        <ArchiveDashboard />
      </section>

      <section
        v-if="selectedSection === 'app-clients'"
        class="app-client-section"
      >
        <AppClientTicketViewer
          v-if="selectedSupportTicket"
          :ticket="selectedSupportTicket"
          :tickets="selectedSupportTickets"
          :client="selectedSupportClient"
          :loading="loadingSupportClient"
          :error-message="supportClientError"
        />

        <AppClientDetails
          v-else
          :refresh-key="appClientRefreshKey"
          :review-counts="reviewCounts" @review-new="openNewReviews" @refresh-reviews="loadReviewCounts"
          @open-ticket="handleOpenSupportTicket"
        />
      </section>

      <section v-if="selectedSection === 'notifications'"><SystemNotifications /></section>

      <section v-if="selectedSection === 'maix-magic'" class="magic-section"><MaixMagicDashboard /></section>

      <section
        v-else-if="selectedSection === 'maix-texts'"
        class="texts-section"
      >
        <MaixMessagesDashboard
          @open-request="openMessageRequest"
          :admin-id="adminId"
          :admin-name="adminName"
        />
      </section>

      <section
        v-else-if="selectedSection === 'requests'"
        class="request-section"
      >
        <!-- Existing request section -->
      </section>

      <section
        v-else-if="selectedSection === 'admins'"
        class="admin-section"
      >
        <!-- existing Admin content -->
      </section>

      <section
        v-if="selectedSection === 'admins'"
        class="admin-section"
      >
        <AdminDetails
          v-if="contentMode === 'list'"
          :refreshKey="adminRefreshKey"
          :can-manage-admins="canManageAdmins"
          @edit="editAdmin"
        />

        <AdminCreator
          v-else-if="contentMode === 'create' || contentMode === 'edit'"
          :admin="selectedAdmin"
          :can-manage-admins="canManageAdmins"
          @cancel="returnToSectionList"
          @saved="handleAdminSaved"
        />
      </section>

      <section
        v-else-if="selectedSection === 'litigations'"
        class="litigation-section"
      >
        <LitigationDetails
          v-if="contentMode === 'list'"
          :refreshKey="litigationRefreshKey"
          @edit="editLitigation"
        />

        <LitigationCreator
          v-else-if="contentMode === 'create'"
          :litigation="selectedLitigation"
          :admin-id="adminId"
          :admin-name="adminName"
          @draft-saved="handleLitigationDraftSaved"
          @removed="handleLitigationRemoved"
          @review-submitted="handleLitigationReviewSubmitted"
        />

        <LitigationEditor
          v-else-if="
            contentMode === 'edit' &&
            selectedLitigation
          "
          :litigation="selectedLitigation"
          :admin-id="adminId"
          :admin-name="adminName"
          @cancel="returnToSectionList"
          @saved="handleLitigationUpdated"
        />
      </section>

      <section
        v-else-if="selectedSection === 'watchlists'"
        class="watchlist-section"
      >
        <WatchlistDetails
          v-if="contentMode === 'list'"
          :refreshKey="watchlistRefreshKey"
          @edit="editWatchlist"
        />

        <WatchlistCreator
          v-else-if="contentMode === 'create'"
          :watchlist="selectedWatchlist"
          :admin-id="adminId"
          :admin-name="adminName"
          @cancel="returnToSectionList"
          @draft-saved="handleWatchlistDraftSaved"
          @removed="handleWatchlistRemoved"
          @review-submitted="handleWatchlistReviewSubmitted"
        />

        <WatchlistEditor
          v-else-if="
            contentMode === 'edit' &&
            selectedWatchlist
          "
          :watchlist="selectedWatchlist"
          :admin-id="adminId"
          :admin-name="adminName"
          @cancel="returnToSectionList"
          @saved="handleWatchlistUpdated"
        />
      </section>

      <section
        v-else-if="selectedSection === 'legal-terms'"
        class="legal-section"
      >
        <LegalTermsDashboard
          :admin-id="adminId"
          :admin-name="adminName"
        />
      </section>

      <section
        v-else-if="selectedSection === 'updates'"
        class="update-section"
      >
        <UpdateDashboard ref="updateDashboard" :admin-id="adminId" />
      </section>

      <section
        v-else-if="selectedSection === 'development'"
        class="development-section"
      >
        <DevelopmentDetailWindow
          :admin-id="adminId"
          :admin-name="adminName"
        />
      </section>

    </section>

  </main>

</div>
</template>

<script>
import { computed, ref } from "vue";
import {
  getAuth,
  onAuthStateChanged
} from "firebase/auth";
import { useRouter } from "vue-router";

import { logOut } from "@/firebase/auth";
import { 
  getAdminInfo, 
  requestSupportClient,
  fetchPreparedSupportClient,
  fetchAttorneyRequests,
  fetchLitigationSubmissions
} from "@/firebase/firestore";

import UiSpacer from "@/components/utils/UiSpacer.vue";

import AdminDetails from "@/components/admins/AdminDetailWindow.vue";
import AdminCreator from "@/components/admins/AdminCreator.vue";

import LitigationDetails from "@/components/litigations/LitigationDetailWindow.vue";
import LitigationCreator from "@/components/litigations/LitigationCreator.vue";
import LitigationEditor from "@/components/litigations/LitigationEditor.vue";

import SubmissionsListWindow from "@/components/submissions/SubmissionsListWindow.vue";
import TrendsListWindow from "@/components/trends/TrendsListWindow.vue";

import WatchlistDetails from "@/components/watchlists/WatchlistDetailWindow.vue";
import WatchlistCreator from "@/components/watchlists/WatchlistCreator.vue";
import WatchlistEditor from "@/components/watchlists/WatchlistEditor.vue";

import UpdateDashboard from "@/components/updates/UpdateDashboard.vue";
import DevelopmentDetailWindow from "@/components/development/DevelopmentDetailWindow.vue";

import SystemNotifications from "@/components/notifications/SystemNotifications.vue";
import MaixMagicDashboard from "@/components/magic/MaixMagicDashboard.vue";
import AttorneySearch from "@/components/attorneyIntake/AttorneySearch.vue";
import RequestDetails from "@/components/requests/RequestDetailWindow.vue";
import RequestDashboard from  "@/components/requests/RequestDashboard.vue";

import AppClientDetails from "@/components/appClient/AppClientDetailWindow.vue";
import AppClientTicketViewer from "@/components/appClient/AppClientTicketViewer.vue";
import ArchiveDashboard from "@/components/archives/ArchiveDashboard.vue";
import LegalTermsDashboard from "@/components/legal/LegalTermsDashboard.vue";
import MaixMessagesDashboard from "@/components/messages/MaixMessagesDashboard.vue";

export default {
  name: "AdminView",

  components: {
    AttorneySearch,
    SystemNotifications,
    MaixMagicDashboard,
    UiSpacer,

    AdminDetails,
    AdminCreator, 
    
    LitigationDetails,
    LitigationCreator,
    LitigationEditor,

    SubmissionsListWindow,
    TrendsListWindow,

    WatchlistDetails,
    WatchlistCreator,
    WatchlistEditor,

    UpdateDashboard,
    DevelopmentDetailWindow,

    RequestDetails,
    RequestDashboard,

    AppClientDetails,
    AppClientTicketViewer,
    ArchiveDashboard,
    LegalTermsDashboard,
    MaixMessagesDashboard
  },

  setup() {

    // MARK: - Router

    const router =
      useRouter();

    // MARK: - Authentication

    const adminId =
      ref("");

    const adminName =
      ref("");

    const isAdmin =
      ref(false);

    const adminRefreshKey =
      ref(0);

    const selectedAdmin =
      ref(null);

    const accessTypes =
      ref([]);

    const canManageAdmins =
      computed(() => accessTypes.value.includes("admin"));

    const canAccessDevelopment =
      computed(() => accessTypes.value.some(role => {
        const normalizedRole = String(role).trim().toLowerCase();

        return normalizedRole === "dev" || normalizedRole === "development";
      }));

    // MARK: - Loading Screen

    const loading =
      ref(true);

    const loadingComplete =
      ref(true);

    const showDashboard =
      ref(false);

    const headline =
      ref(
        "Lomax Law Firm Connect"
      );

    const subHeadline =
      ref(
        "Admin Portal"
      );

    // MARK: - Sidebar Navigation

    const reviewCounts = ref({ requests: null, submissions: null });
    const reviewFilter = ref("");
    const reviewFilterKey = ref(0);
    const loadReviewCounts = async () => {
      const results = await Promise.allSettled([
        fetchAttorneyRequests().then(rows => { reviewCounts.value.requests = rows.filter(row => row.status === "new").length; }),
        fetchLitigationSubmissions().then(rows => { reviewCounts.value.submissions = rows.filter(row => row.status === "new").length; })
      ]);
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          reviewCounts.value[index === 0 ? "requests" : "submissions"] = null;
          console.warn("Unable to refresh review counts", result.reason);
        }
      });
    };
    const openNewReviews = section => {
      selectSection(section);
      reviewFilter.value = "new";
      reviewFilterKey.value++;
    };

    const selectedSection =
      ref(
        "requests"
      );

    const updateDashboard =
      ref(null);

    const sidebarCollapsed =
      ref(
        window.innerWidth <= 800
      );

    const toggleSidebar =
      () => {

        sidebarCollapsed.value =
          !sidebarCollapsed.value;
      };

    /*
     list:
     Show the selected section's normal viewer.

     create:
     Show that section's creation form.
     */
    const contentMode =
      ref(
        "list"
      );

    const loadingSupportClient = 
      ref(false);
    const supportClientError = 
      ref("");

    const allNavigationItems = [
      { id: "attorney-search", group: "lomax-connect", title: "Attorney Network", icon: "person_search", description: "Find attorney referral applications by name, jurisdiction and practice area.", action: null },
      {
        id:
          "requests",
        group:
          "activity",
        title:
          "Requests",
        icon:
          "inbox",
        description:
          "Review and manage client requests for attorney assistance.",
        action:
          null
      },
      {
        id:
          "submissions",
        group:
          "activity",
        title:
          "Submissions",
        icon:
          "assignment_turned_in",
        description:
          "Review completed Litigation applications.",
        action:
          null
      },
      {
        id:
          "watch-trends",
        group:
          "activity",
        title:
          "Watch Trends",
        icon:
          "insights",
        description:
          "Explore Watch activity and emerging Litigation trends.",
        action:
          null
      },
      {
        id:
          "archives",
        group:
          "activity",
        title:
          "Archives",
        icon:
          "inventory_2",
        description:
          "Review archived App Clients, open Litigation matters, and required follow-up.",
        action:
          null
      },
      {
        id:
          "app-clients",
        group:
          "maix",
        title:
          "Maix Legal",
        icon:
          "groups",
        description:
          "Search App Clients and Review App Activity.",
        action:
          null
      },
      {
        id:
          "maix-texts",
        group:
          "maix",
        title:
          "Maix Message",
        icon:
          "sms",
        description:
          "Review app, email, and text messages with App Clients.",
        action:
          null
      },
      { id: "maix-magic", group: "maix", title: "Maix Magic", icon: "auto_awesome",
        description: "Draft client responses and build your reusable response library.", action: null },
      {
        id:
          "litigations",
        group:
          "app-management",
        title:
          "Litigations",
        icon:
          "gavel",
        description:
          "Create and manage litigation records shown in the app.",
        action:
          "Add Litigation"
      },
      {
        id:
          "watchlists",
        group:
          "app-management",
        title:
          "Watchlists",
        icon:
          "visibility",
        description:
          "Review watchlist activity and monitored matters.",
        action:
          null
      },
      {
        id:
          "legal-terms",
        group:
          "app-management",
        title:
          "Legal + Terms",
        icon:
          "policy",
        description:
          "Create and version the shared legal language used by the website and mobile apps.",
        action:
          null
      },
      {
        id:
          "updates",
        group:
          "system",
        title:
          "Updates",
        icon:
          "update",
        description:
          "Review CourtListener activity and publish approved Litigation updates.",
        action:
          null
      },
      { id: "notifications", group: "system", title: "Notifications", icon: "notifications",
        description: "Manage your subscriptions to system emails and review cloud email workflows.", action: null },
      {
        id:
          "development",
        group:
          "system",
        title:
          "Development",
        icon:
          "code",
        description:
          "Development tools and diagnostics.",
        action:
          null,
        requiresDevelopmentRole:
          true
      },
      {
        id:
          "admins",
        group:
          "system",
        title:
          "Admins",
        icon:
          "manage_accounts",
        description:
          "Manage admin users, permissions, and access.",
        action:
          "Add Admin"
      }
    ];

    const navigationItems =
      computed(() => allNavigationItems.filter(item => {
        return !item.requiresDevelopmentRole || canAccessDevelopment.value;
      }));

    const navigationGroups =
      computed(() => [
        {
          id: "maix",
          title: "",
          items: navigationItems.value.filter(item => item.group === "maix")
        },
        {
          id: "activity",
          title: "Activity",
          items: navigationItems.value.filter(item => item.group === "activity")
        },
        {
          id: "app-management",
          title: "App Management",
          items: navigationItems.value.filter(item => item.group === "app-management")
        },
        {
          id: "lomax-connect",
          title: "Lomax Connect",
          items: navigationItems.value.filter(item => item.group === "lomax-connect")
        },
        {
          id: "system",
          title: "System",
          items: navigationItems.value.filter(item => item.group === "system")
        }
      ].filter(group => group.items.length > 0));

    const selectedNavigationItem =
      computed(
        () => {

          return navigationItems.value.find(
            item =>
              item.id ===
              selectedSection.value
          );
        }
      );

    const selectedSectionTitle =
      computed(
        () => {

          return (
            selectedNavigationItem
              .value
              ?.title ??
            "Admin"
          );
        }
      );

    const selectedSectionDescription =
      computed(
        () => {

          return (
            selectedNavigationItem
              .value
              ?.description ??
            ""
          );
        }
      );

    const selectedSectionAction =
      computed(() => {

        if (
          selectedSection.value ===
          "admins"
        ) {

          if (!canManageAdmins.value) {
            return null;
          }

          return contentMode.value !==
            "list"
            ? "Admins"
            : "Add Admin";
        }

        if (
          selectedSection.value ===
          "litigations"
        ) {

          return contentMode.value ===
            "list"
            ? "Add Litigation"
            : "Litigations";
        }

        if (
          selectedSection.value ===
          "watchlists"
        ) {

          return contentMode.value ===
            "list"
            ? "Add Watchlist"
            : "Watchlists";
        }

        if (
          selectedSection.value ===
          "updates"
        ) {

          return "Add Monitor";
        }

        if (
          selectedSection.value ===
          "app-clients"
        ) {

          if (selectedSection.value === "app-clients") {
            return selectedSupportTicket.value
              ? "Go Back"
              : "Refresh";
          }
        }

      return null;
        });

    const selectSection =
      sectionId => {

        const navigationItem =
          navigationItems.value.find(item => item.id === sectionId);

        if (!navigationItem || navigationItem.placeholder) {
          return;
        }

        reviewFilter.value = "";
        selectedSection.value =
          sectionId;

        contentMode.value =
          "list";

        selectedAdmin.value =
          null;

        if (
          sectionId ===
          "app-clients"
        ) {

          appClientMode.value =
            "dashboard";

          selectedAppClient.value =
            null;
        }
      };

    const performSelectedSectionAction =
      () => {

        switch (
          selectedSection.value
        ) {

        case "admins":

        if (!canManageAdmins.value) {
          return;
        }

        if (contentMode.value === "list") {
          selectedAdmin.value = null;
          contentMode.value = "create";
        } else {
          returnToSectionList();
        }

        break;

        case "litigations":

          if (
            contentMode.value ===
            "list"
          ) {

            contentMode.value =
              "create";

            selectedLitigation.value =
              null;

          } else {
            returnToSectionList();
          }

        break;

        case "watchlists":

        if (
          contentMode.value ===
          "list"
        ) {

          contentMode.value =
            "create";

          selectedWatchlist.value =
            null;

        } else {

          returnToSectionList();
        }

        break;

        case "app-clients":

        if (selectedSupportTicket.value) {
          returnToAppClientDashboard();
        } else {
          refreshAppClientDashboard();
        }

        break;

        case "updates":

          updateDashboard.value?.openAddMonitor?.();

        break;

        default:

        break;
        }
      };

    const returnToSectionList =
      () => {

        contentMode.value =
          "list";

        selectedRequest.value =
          null;

        selectedLitigation.value =
          null;

        selectedWatchlist.value =
          null;

        selectedAdmin.value =
          null;
      };

    const editAdmin =
      adminRecord => {

        if (!canManageAdmins.value) {
          return;
        }

        selectedAdmin.value =
          adminRecord;

        contentMode.value =
          "edit";
      };

    const handleAdminSaved =
      () => {

        contentMode.value =
          "list";

        selectedAdmin.value =
          null;

        adminRefreshKey.value +=
          1;
      };

    const selectedLitigation =
      ref(null);

    const litigationRefreshKey =
      ref(0);

    const editLitigation =
      litigation => {

        selectedLitigation.value =
          litigation;

        contentMode.value =
          litigation.publicationStatus === "draft" ?
            "create" :
            "edit";
      };

    const handleLitigationUpdated =
    () => {
      selectedLitigation.value = null;
      contentMode.value = "list";
      litigationRefreshKey.value += 1;
    };

    const handleLitigationDraftSaved =
    () => {
      litigationRefreshKey.value += 1;
    };

    const handleLitigationReviewSubmitted =
    () => {
      selectedLitigation.value = null;
      contentMode.value = "list";
      litigationRefreshKey.value += 1;
    };

    const handleLitigationRemoved =
    () => {
      selectedLitigation.value = null;
      contentMode.value = "list";
      litigationRefreshKey.value += 1;
    };


    const selectedWatchlist = 
      ref(null);
    
    const watchlistRefreshKey =
      ref(0);

    const editWatchlist =
      watchlist => {

        selectedWatchlist.value =
          watchlist;

        contentMode.value =
          watchlist.publicationStatus === "draft" ?
            "create" :
            "edit";
      };

    const handleWatchlistDraftSaved =
    () => {
      watchlistRefreshKey.value +=
        1;
    };

    const handleWatchlistReviewSubmitted =
    () => {
      selectedWatchlist.value = null;
      contentMode.value = "list";
      watchlistRefreshKey.value += 1;
    };

    const handleWatchlistRemoved =
    () => {
      selectedWatchlist.value = null;
      contentMode.value = "list";
      watchlistRefreshKey.value += 1;
    };

    const handleWatchlistUpdated =
    () => {

      selectedWatchlist.value =
        null;

      contentMode.value =
        "list";

      watchlistRefreshKey.value +=
        1;
    };


    const selectedRequest =
      ref(null);

    const requestRefreshKey =
      ref(0);

    const reviewRequest =
      request => {

        selectedRequest.value =
          request;

        contentMode.value =
          "review";
      };

    const openMessageRequest = request => {
      selectedSection.value = "requests";
      reviewRequest(request);
    };

    const handleRequestSaved =
      updatedRequest => {
        loadReviewCounts();

        selectedRequest.value =
          updatedRequest;

        requestRefreshKey.value +=
          1;
      };
    
    const handleRequestChat =
      request => {

        console.log(
          "Create or open request chat:",
          request
        );
      };

    const appClientMode =
      ref("dashboard");

    const selectedAppClient =
      ref(null);

    const appClientRefreshKey =
      ref(0);

    const selectedSupportTicket = 
      ref(null);

    const selectedSupportClient = 
      ref(null);

    const selectedSupportTickets = 
      ref([]);

    const openAppClient =
      client => {

        selectedAppClient.value =
          client;

        appClientMode.value =
          "client";
      };

      const handleOpenSupportTicket =
        async ticket => {

          selectedSupportTicket.value = ticket;
          selectedSupportClient.value = null;

          loadingSupportClient.value = true;
          supportClientError.value = "";

          try {

            const requestId =
              await requestSupportClient(
                ticket,
                adminId.value
              );

            let prepared = null;

            for (let attempt = 0; attempt < 12; attempt += 1) {

              prepared =
                await fetchPreparedSupportClient(
                  requestId
                );

                if (prepared) { break; }

                await new Promise(resolve =>
                  setTimeout(resolve, 500)
                );
            }

            if (!prepared?.client) {
              throw new Error(
                "The App Client is still being prepared."
              );
            }

            selectedSupportClient.value =
              prepared.client;

            selectedSupportTickets.value =
              prepared.tickets;

          } catch (error) {

            console.error(
              "❌ Unable to load Support Client:",
              error
            );

            supportClientError.value =
              error?.message ??
              "Unable to load the App Client.";

          } finally {

            loadingSupportClient.value = false;
          }
        };

    const returnToAppClientDashboard =
      () => {

        selectedSupportTicket.value = null;
        selectedSupportClient.value = null;
        selectedSupportTickets.value = [];

        selectedAppClient.value = null;
        appClientMode.value = "dashboard";
      };

    const refreshAppClientDashboard =
      () => {

        appClientRefreshKey.value +=
          1;
      };

    // MARK: - Authentication Listener

    onAuthStateChanged(
      getAuth(),
      async user => {

        if (!user) {

          console.log(
            "❌ No user logged in"
          );

          await router.push(
            "/"
          );

          return;
        }

        try {

          console.log(
            "🔄 Checking admin access for:",
            user.email
          );

          const adminInfo =
            await getAdminInfo(
              user.uid
            );

          isAdmin.value =
            adminInfo?.isAdmin ??
            false;

          accessTypes.value =
            adminInfo?.accessTypes ??
            [];

          adminName.value =
            adminInfo?.name ??
            "";

          if (!isAdmin.value) {

            console.warn(
              "❌ Access denied: user is not an admin."
            );

            await logOut();

            await router.push(
              "/"
            );

            return;
          }

          adminId.value =
            user.uid;

          loadingComplete.value =
            false;

        } catch (error) {

          console.error(
            "❌ Unable to initialize the admin portal:",
            error
          );

          loadingComplete.value =
            false;
        }
      }
    );

    // MARK: - Dashboard Start

    const startDashboard =
      () => {
        selectSection("app-clients");
        loadReviewCounts();

        showDashboard.value =
          true;

        loading.value =
          false;
      };

    // MARK: - Log Out

    const handleLogOut =
      async () => {

        try {

          await logOut();

          console.log(
            "✅ User logged out"
          );

          await router.push(
            "/"
          );

        } catch (error) {

          console.error(
            "❌ Error logging out:",
            error
          );
        }
      };

    return {

      // Authentication
      adminId,
      adminName,
      isAdmin,
      accessTypes,
      canManageAdmins,
      canAccessDevelopment,

      // Loading
      loading,
      loadingComplete,
      showDashboard,
      reviewCounts, reviewFilter, reviewFilterKey, openNewReviews, loadReviewCounts,
      headline,
      subHeadline,
      startDashboard,

      // Sidebar
      navigationItems,
      navigationGroups,
      sidebarCollapsed,
      toggleSidebar,
      selectedSection,
      selectedSectionTitle,
      selectedSectionDescription,
      selectedSectionAction,
      selectSection,
      updateDashboard,

      // Right-side content
      contentMode,
      performSelectedSectionAction,
      returnToSectionList,

      adminRefreshKey,
      selectedAdmin,
      editAdmin,
      handleAdminSaved,

      selectedLitigation,
      litigationRefreshKey,
      editLitigation,
      handleLitigationUpdated,
      handleLitigationDraftSaved,
      handleLitigationRemoved,
      handleLitigationReviewSubmitted,

      selectedWatchlist,
      watchlistRefreshKey,
      editWatchlist,
      handleWatchlistDraftSaved,
      handleWatchlistRemoved,
      handleWatchlistReviewSubmitted,
      handleWatchlistUpdated,

      requestRefreshKey,
      selectedRequest,
      openMessageRequest,
      reviewRequest,
      handleRequestSaved,
      handleRequestChat,

      appClientMode,
      selectedAppClient,
      appClientRefreshKey,
      selectedSupportTicket,
      selectedSupportClient,
      selectedSupportTickets,
      openAppClient,
      loadingSupportClient,
      supportClientError,
      handleOpenSupportTicket,
      returnToAppClientDashboard,
      refreshAppClientDashboard,

      // Session
      handleLogOut
    };
  }
};
</script>

<style scoped>

.admin-shell {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-columns: 270px minmax(0, 1fr);
  width: auto;
  height: auto;
  gap: 18px;
  padding: 18px;
  box-sizing: border-box;
  overflow: hidden;
  background: #eef1ef;
  color: #18231f;
  transition: grid-template-columns 0.2s ease;
}

.admin-shell.sidebar-collapsed {
  grid-template-columns: 88px minmax(0, 1fr);
}

.admin-sidebar,
.admin-content {
  min-width: 0;
  min-height: 0;
  background: #ffffff;
  border: 1px solid #dde2df;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(20, 40, 30, 0.06);
}

/* Sidebar */

.admin-sidebar {
  display: flex;
  flex-direction: column;
}

.sidebar-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 24px 22px 20px;
  border-bottom: 1px solid #e7ebe8;
}

.sidebar-brand {
  min-width: 0;
}

.sidebar-heading h1 {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
}

.sidebar-heading p {
  margin: 5px 0 0;
  color: #7a847f;
  font-size: 0.9rem;
}

.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 31px;
  height: 31px;
  flex-shrink: 0;
  padding: 0;
  border: 1px solid #d9e7df;
  border-radius: 8px;
  background: #f2f8f4;
  color: #159c62;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.sidebar-toggle:hover {
  border-color: #c8dfd2;
  background: #e7f3eb;
}

.sidebar-toggle .material-icons-outlined {
  font-size: 19px;
}

.sidebar-navigation {
  flex: 1;
  padding: 8px 0 14px;
  overflow-y: auto;
}

.sidebar-group {
  padding: 12px 0 8px;
}

.sidebar-group + .sidebar-group {
  border-top: 1px solid #edf0ee;
}

.sidebar-group-heading {
  margin: 0;
  padding: 0 22px 7px;
  color: #8a938f;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  line-height: 1.2;
  text-transform: uppercase;
}

.sidebar-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  padding: 15px 22px;
  border: 0;
  background: transparent;
  color: #29342f;
  font: inherit;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.sidebar-item:hover {
  background: #f4f8f5;
}

.sidebar-item.placeholder {
  cursor: default;
}

.sidebar-item.placeholder:hover {
  background: transparent;
}

.sidebar-item.active {
  background: #f2f8f4;
  color: #159c62;
  font-weight: 600;
}

.sidebar-item.active::before {
  position: absolute;
  top: 7px;
  bottom: 7px;
  left: 0;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: #20b875;
  content: "";
}

.sidebar-icon {
  flex-shrink: 0;
  font-size: 21px;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid #e7ebe8;
}

.logout-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d8dedb;
  border-radius: 10px;
  background: #ffffff;
  color: #a33636;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.logout-button:hover {
  background: #fff5f5;
  border-color: #e3bcbc;
}

.sidebar-collapsed .sidebar-heading {
  align-items: center;
  flex-direction: column;
  padding: 18px 8px 14px;
  text-align: center;
}

.sidebar-collapsed .sidebar-brand {
  width: 100%;
}

.sidebar-collapsed .sidebar-heading h1,
.sidebar-collapsed .sidebar-heading p {
  font-size: 0.78rem;
  font-weight: 700;
}

.sidebar-collapsed .sidebar-heading p {
  margin-top: 3px;
}

.sidebar-collapsed .sidebar-item {
  justify-content: center;
  padding: 16px 10px;
}

.sidebar-collapsed .sidebar-item-label,
.sidebar-collapsed .sidebar-group-heading,
.sidebar-collapsed .logout-label {
  display: none;
}

.sidebar-collapsed .sidebar-group {
  padding: 8px 0;
}

.sidebar-collapsed .sidebar-footer {
  padding: 12px;
}

.sidebar-collapsed .logout-button {
  padding: 12px 8px;
}

/* Main content */

.admin-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.content-header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 96px;
  padding: 20px 26px;
  border-bottom: 1px solid #e7ebe8;
}

.content-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.content-header p {
  margin: 5px 0 0;
  color: #7b8580;
}

.primary-action-button {
  flex-shrink: 0;
  padding: 11px 18px;
  border: 0;
  border-radius: 9px;
  background: #18ab69;
  color: #ffffff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.primary-action-button:hover {
  background: #15945c;
}

.content-window {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  padding: 26px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #fbfcfb;
}

.content-window.legal-content-window {
  overflow-y: auto;
}

.content-window.texts-content-window {
  padding: 18px;
  overflow: hidden;
}

.content-window.request-review-content-window {
  padding: 0;
  overflow: hidden;
}

.request-review-content-window .request-section {
  height: 100%;
  min-height: 0;
}

.texts-section {
  height: 100%;
  min-height: 0;
}

.section-placeholder {
  min-height: 300px;
  padding: 30px;
  border: 1px solid #e2e7e4;
  border-radius: 14px;
  background: #ffffff;
}

.section-placeholder h3 {
  margin: 0 0 10px;
}

.section-placeholder p {
  margin: 0;
  color: #737d78;
}

.admin-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.admin-section,
.litigation-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.litigation-section {
  height: 100%;
}

.admin-section,
.litigation-section,
.watchlist-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.litigation-section,
.watchlist-section {
  height: 100%;
}

.request-section,
.admin-section,
.litigation-section,
.watchlist-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.request-section,
.litigation-section,
.watchlist-section {
  height: 100%;
}

.app-client-section,
.request-section,
.admin-section,
.litigation-section,
.watchlist-section,
.legal-section,
.update-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.app-client-section,
.request-section,
.litigation-section,
.watchlist-section,
.legal-section,
.update-section {
  height: 100%;
}

.legal-section {
  flex: none;
  height: auto;
  min-height: 100%;
}

/* Responsive */

@media (max-width: 800px) {
  .admin-shell {
    gap: 10px;
    padding: 10px;
  }

  .content-header {
    align-items: flex-start;
    min-height: auto;
    padding: 18px;
  }

  .content-window {
    flex: 1;
    min-height: 0;
    padding: 26px;
    overflow: hidden;
    background: #fbfcfb;
  }
}

@media (max-width: 560px) {
  .admin-shell.sidebar-collapsed {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .content-header {
    flex-direction: column;
  }

  .primary-action-button {
    width: 100%;
  }
}
</style>

<style scoped>
.sidebar-item { position: relative; }
.review-badge { margin-left: auto; border-radius: 20px; min-width: 22px; padding: 2px 6px; background: #b94b24; color: white; font-size: .72rem; font-weight: 700; text-align: center; }
.sidebar-collapsed .review-badge { position: absolute; top: 3px; right: 5px; margin: 0; min-width: 18px; font-size: .65rem; }
</style>

<style scoped>
.magic-section { height: 100%; min-height: 0; }
</style>
